import { format } from 'date-fns';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import { Typography } from '@mui/material';
import type { AuditLogEntry } from '@core/api/auditLog';
import { renderAuditLogSummary } from '@core/audit';
import { ACTOR_TYPE_LABELS, ActorType } from '@core/enum/actorType';
import { hasOrderHistoryDetails } from '../../audit/hasOrderHistoryDetails';

/** MUI's dot has an 11.5px vertical margin. This overlap meets the next dot. */
const TIMELINE_CONNECTOR_OVERLAP = '-11.5px';

export function HistoryTimelineRow({
  entry,
  gapLabel,
  isLatest,
  showConnector,
  onOpen,
}: {
  entry: AuditLogEntry;
  gapLabel: string | null;
  isLatest: boolean;
  showConnector: boolean;
  onOpen: (entry: AuditLogEntry) => void;
}) {
  const hasDetails = hasOrderHistoryDetails(entry);

  return (
    <TimelineItem>
      <TimelineOppositeContent
        sx={{
          flex: 0.35,
          maxWidth: { xs: 96, sm: 140 },
          px: { xs: 0, sm: 1 },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {format(new Date(entry.createdAt), 'MMM d, yyyy')}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {format(new Date(entry.createdAt), 'h:mm a')}
        </Typography>
        {gapLabel ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 'auto', pt: 8, fontStyle: 'italic' }}
          >
            {gapLabel}
          </Typography>
        ) : null}
      </TimelineOppositeContent>
      <TimelineSeparator>
        <TimelineDot color={isLatest ? 'primary' : 'grey'} />
        {showConnector ? (
          <TimelineConnector sx={{ mb: TIMELINE_CONNECTOR_OVERLAP }} />
        ) : null}
      </TimelineSeparator>
      <TimelineContent sx={{ py: 1, px: { xs: 1, sm: 2 } }}>
        <Typography
          variant="body2"
          fontWeight={600}
          color="text.primary"
          component={hasDetails ? 'button' : 'p'}
          onClick={hasDetails ? () => onOpen(entry) : undefined}
          sx={
            hasDetails
              ? {
                  display: 'block',
                  p: 0,
                  border: 0,
                  bgcolor: 'transparent',
                  textAlign: 'left',
                  '&:hover': { textDecoration: 'underline' },
                }
              : undefined
          }
        >
          {renderAuditLogSummary(entry)}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {entry.actorType === ActorType.User
            ? entry.actor
              ? `${entry.actor.firstName} ${entry.actor.lastName}`.trim()
              : 'Unknown user'
            : ACTOR_TYPE_LABELS[entry.actorType]}
        </Typography>
      </TimelineContent>
    </TimelineItem>
  );
}
